/**
 * Chat store — persistent-conversation actions.
 *
 * Owns the network calls that fetch / switch / archive saved threads, plus the
 * one-shot defaults hydration at boot. Lives in its own module so the message
 * decoder + generation-guard logic don't bloat the action surface.
 *
 * Dependencies run one way: this module uses the shared state + mutations and
 * the summarize watch (handing it `reloadAfterSummary`); the action object
 * (`chatStore.actions`) composes this module, never the reverse.
 */
import { apiFetch } from './api';
import { addError } from './chatStore.mutations.js';
import { bumpSwitchGeneration, getState, getSwitchGeneration, makeId, setState } from './chatStore.internal.js';
import { stopSummarizePolling, syncSummarizeJob } from './chatStore.summarize.js';
import { clearLastConversationId, getLastConversationId, setLastConversationId } from './lastConversation.js';
import type { ChatMessage, ConversationSummary, StreamSegment } from './chatStore.types.js';
import { isToolWarning } from '@onestone/shared/toolWarning';
import type { SessionMessagesResponse } from '@onestone/shared/responses';
import { roleLabel, type StoredContentBlock, type StoredMessage } from '@onestone/shared/conversationsIO';

export async function loadConversations(): Promise<void> {
  try {
    const res = await apiFetch(`/api/chat/sessions`);
    if (!res.ok) return;
    const data = (await res.json()) as { sessions: ConversationSummary[] };
    setState({ ...getState(), conversations: data.sessions, conversationsLoaded: true });
  } catch {
    // Server not running — silently ignore
  }
}

/**
 * Switch to a saved conversation and load its messages.
 *
 * The view resets synchronously: the new ID is committed before the fetch so
 * the switcher highlights immediately, and the previous thread's in-flight
 * stream is aborted client-side so its events can't bleed cost/usage/meta
 * into this conversation. (The turn keeps running server-side and persists
 * on completion.) A generation guard drops stale responses when the operator
 * switches again before the fetch resolves — last click always wins.
 */
export async function switchConversation(id: string): Promise<void> {
  const generation = bumpSwitchGeneration();
  let s = getState();
  if (s.conversationId !== id) {
    s.abortController?.abort();
    stopSummarizePolling();
    setState({
      ...s,
      conversationId: id,
      messages: [],
      earlier: null,
      isStreaming: false,
      abortController: null,
      summarizeJob: null,
      conversationMeta: {},
      explicitSelection: false,
      useExtendedContext: false,
      routeReason: '',
      routeEffort: '',
      turnMeta: null,
      pendingQuestion: null,
    });
  }
  try {
    const res = await apiFetch(messagesPath(id));
    if (generation !== getSwitchGeneration()) return; // superseded — drop stale response
    if (!res.ok) {
      addError(`Failed to load conversation: ${res.status} ${res.statusText}`);
      return;
    }
    const data = (await res.json()) as SessionMessagesResponse;
    if (generation !== getSwitchGeneration()) return; // superseded — drop stale response

    const messages = decodeMessages(data.messages);

    s = getState();
    setState({
      ...s,
      conversationId: id,
      messages,
      earlier: data.earlier,
      isStreaming: false,
      totalCost: data.meta?.totalCost ?? 0,
      provider: data.meta?.provider ?? s.provider,
      model: data.meta?.model ?? s.model,
      useExtendedContext: data.meta?.useExtendedContext === true,
      conversationMeta: {
        ...(typeof data.meta?.messageCount === 'number' ? { messageCount: data.meta.messageCount } : {}),
        // Hydrate the MEASURED fill so a reopened conversation shows real occupancy
        // immediately (server persists it as lastRequestPromptTokens).
        ...(typeof data.meta?.lastRequestPromptTokens === 'number' && data.meta.lastRequestPromptTokens > 0
          ? { lastPromptTokens: data.meta.lastRequestPromptTokens }
          : {}),
        ...(data.meta?.loadedSkills && data.meta.loadedSkills.length > 0
          ? { loadedSkills: data.meta.loadedSkills }
          : {}),
      },
    });
    // Bookmark the thread we successfully landed on — next app launch
    // restores here instead of opening a fresh empty conversation.
    setLastConversationId(id);
    // Re-attach to an in-flight summarize job (reload survival) — only
    // adopts running jobs, so terminal states never re-toast on hydrate.
    void syncSummarizeJob(id, reloadAfterSummary);
    // Re-adopt a pending ask_operator question (gap-reload rehydration) — a
    // client returning past the SSE buffer still renders the card.
    void syncPendingQuestion(id);
  } catch (e) {
    if (generation !== getSwitchGeneration()) return;
    addError(`Failed to load conversation: ${e instanceof Error ? e.message : 'network error'}`);
  }
}

/** The messages route: the newest page, or the page before log index `before`. */
function messagesPath(id: string, before?: number): string {
  const base = `/api/chat/sessions/${encodeURIComponent(id)}/messages`;
  return before === undefined ? base : `${base}?before=${before}`;
}

/** One earlier page of a conversation, decoded. Throws on a failed request. */
async function fetchPage(id: string, before: number): Promise<{ messages: ChatMessage[]; earlier: number | null }> {
  const res = await apiFetch(messagesPath(id, before));
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = (await res.json()) as SessionMessagesResponse;
  return { messages: decodeMessages(data.messages), earlier: data.earlier };
}

/**
 * "Show earlier messages": load the page above the loaded messages and put it
 * in front. Dropped when the conversation changed meanwhile, or another call
 * already loaded that page. True when a page was added.
 */
export async function loadEarlierMessages(): Promise<boolean> {
  const { conversationId, earlier } = getState();
  if (earlier === null) return false;
  const generation = getSwitchGeneration();
  try {
    const page = await fetchPage(conversationId, earlier);
    const s = getState();
    if (generation !== getSwitchGeneration() || s.earlier !== earlier) return false;
    setState({ ...s, messages: [...page.messages, ...s.messages], earlier: page.earlier });
    return true;
  } catch (e) {
    if (generation === getSwitchGeneration()) {
      addError(`Failed to load earlier messages: ${e instanceof Error ? e.message : 'network error'}`);
    }
    return false;
  }
}

/**
 * Every message of the loaded conversation: the pages above the loaded ones
 * fetched first (an export must not quietly cover only the newest page).
 */
export async function loadWholeConversation(): Promise<ChatMessage[]> {
  const { conversationId, earlier, messages } = getState();
  const pages: ChatMessage[][] = [messages];
  for (let cursor = earlier; cursor !== null;) {
    const page = await fetchPage(conversationId, cursor);
    pages.unshift(page.messages);
    cursor = page.earlier;
  }
  return pages.flat();
}

/**
 * Archive a conversation — the server moves its directory into `.archive/`
 * (non-destructive, recoverable via the restore endpoint). The DELETE verb
 * is historical; the action is archive, never destructive.
 */
export async function archiveConversation(id: string): Promise<void> {
  try {
    await apiFetch(`/api/chat/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (getLastConversationId() === id) clearLastConversationId();
    const s = getState();
    let next = { ...s, conversations: s.conversations.filter((c) => c.id !== id) };
    if (s.conversationId === id) {
      bumpSwitchGeneration(); // invalidate any in-flight load of the archived thread
      next = {
        ...next,
        messages: [],
        earlier: null,
        conversationId: crypto.randomUUID(),
        totalCost: 0,
      };
    }
    setState(next);
  } catch {
    // Silently fail
  }
}

/**
 * Adopt the server's Settings-resolved default agent for display. Runs once
 * at boot; skipped if the operator already picked explicitly or a
 * conversation is loaded (those carry their own pinned model).
 */
export async function hydrateDefaults(): Promise<void> {
  try {
    const res = await apiFetch(`/api/models`);
    if (!res.ok) return;
    const data = (await res.json()) as {
      default: string | null;
      defaultExecutor: 'cli' | 'api';
      channelProviders: { dashboard: string };
    };
    const s = getState();
    if (s.explicitSelection || s.messages.length > 0 || s.model !== '') return;
    // Only pre-populate if the server has an explicit dashboard-channel default.
    // When null, the picker stays empty — the user must choose a model before
    // their first message (the routing chain returns 422 without a selection).
    if (!data.default) return;
    // The default runs on the Conversation channel's source (Settings → Model).
    const provider = data.defaultExecutor === 'cli' ? 'subscription' : data.channelProviders.dashboard;
    setState({ ...s, provider, model: data.default });
  } catch {
    // Server not running — picker stays empty until a route event arrives
  }
}

/**
 * Boot-time restore: reopen the conversation the operator was last in.
 *
 * Reads the localStorage bookmark, verifies the thread still exists
 * server-side (it may have been archived from another window or swept), and
 * switches to it. Bails without touching the view if the operator already
 * started interacting — the restore must never yank a thread out from under
 * a fast typist. A dead bookmark is cleared so the next boot is clean.
 */
export async function restoreLastConversation(): Promise<void> {
  const id = getLastConversationId();
  if (!id) return;
  try {
    const res = await apiFetch(`/api/chat/sessions`);
    if (!res.ok) return;
    const data = (await res.json()) as { sessions: ConversationSummary[] };
    setState({ ...getState(), conversations: data.sessions, conversationsLoaded: true });
    if (!data.sessions.some((c) => c.id === id)) {
      clearLastConversationId();
      return;
    }
    const s = getState();
    if (s.messages.length > 0 || s.isStreaming) return; // operator got there first
    await switchConversation(id);
  } catch {
    // Server not running — open on the usual fresh conversation.
  }
}

// ---------------------------------------------------------------------------
// Decoder
// ---------------------------------------------------------------------------

/** Turn the wire-format message log into the renderer's segment-based model. */
function decodeMessages(persisted: StoredMessage[]): ChatMessage[] {
  const messages: ChatMessage[] = [];

  for (const m of persisted) {
    // A checkpoint renders in place: the summary that stands in for everything above it.
    const label = roleLabel(m);
    if (label === 'checkpoint' && typeof m.content === 'string') {
      messages.push({
        id: makeId(),
        role: 'checkpoint',
        content: m.content,
        timestamp: m.timestamp,
        ...(typeof m.covers === 'number' ? { covers: m.covers } : {}),
      });
      continue;
    }
    if (typeof m.content === 'string') {
      messages.push({
        id: makeId(),
        role: m.role,
        content: m.content,
        ...(m.attachments?.length ? { attachments: m.attachments } : {}),
      });
      continue;
    }

    // Tool results are persisted as separate user-role messages (API turn
    // structure). Attach them to the matching tool_call segments of prior
    // assistant messages instead of rendering empty user bubbles.
    if (label === 'tool') {
      for (const block of m.content) {
        if (block.type !== 'tool_result') continue;
        attachToolResultToPrior(messages, block);
      }
      continue;
    }

    const segments: StreamSegment[] = [];
    for (const block of m.content) {
      if (block.type === 'text') {
        segments.push({ type: 'text', content: block.text });
      } else if (block.type === 'tool_use') {
        segments.push({
          type: 'tool_call',
          id: block.id,
          name: block.name,
          input: JSON.stringify(block.input, null, 2),
        });
      } else if (block.type === 'tool_result') {
        const match = segments.find((seg) => seg.type === 'tool_call' && seg.id === block.tool_use_id);
        if (match && match.type === 'tool_call') {
          applyToolResultToSegment(match, block);
        }
      }
      // thinking / redacted_thinking / any future block type: intentionally
      // not rendered — skip.
    }
    // A message whose blocks all decoded to nothing (thinking-only, or an
    // unknown future type) must be dropped entirely: an empty assistant
    // bubble renders as the live-stream "Thinking…" placeholder forever.
    if (segments.length === 0) continue;
    messages.push({ id: makeId(), role: m.role, segments });
  }

  return messages;
}

function attachToolResultToPrior(
  messages: ChatMessage[],
  block: Extract<StoredContentBlock, { type: 'tool_result' }>,
): void {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (!msg) continue;
    const match = msg.segments?.find((seg) => seg.type === 'tool_call' && seg.id === block.tool_use_id);
    if (match && match.type === 'tool_call') {
      applyToolResultToSegment(match, block);
      return;
    }
  }
}

/**
 * Map a tool_result block's payload onto its matched tool_call segment — the
 * single place that defines how output + error state attach (MA-196: both the
 * cross-message backfill above and the in-message load path below shared this
 * mutation verbatim).
 */
function applyToolResultToSegment(
  segment: Extract<StreamSegment, { type: 'tool_call' }>,
  block: Pick<Extract<StoredContentBlock, { type: 'tool_result' }>, 'content' | 'is_error'>,
): void {
  segment.output = block.content;
  segment.isError = block.is_error ?? false;
  // Detect warning state for both new and historical data
  if (!segment.isError) {
    segment.isWarning = isToolWarning(segment.name, block.content);
  }
}

/**
 * After a summary is written: re-load the conversation from server truth (the
 * summary card) and refresh the list. Handed to the summarize watch.
 */
export async function reloadAfterSummary(conversationId: string): Promise<void> {
  await switchConversation(conversationId);
  void loadConversations();
}

/**
 * Gap-reload rehydration: re-adopt a pending question after a reload or
 * conversation switch that happened past the SSE buffer. Server truth wins —
 * null clears any stale card.
 */
export async function syncPendingQuestion(conversationId: string): Promise<void> {
  try {
    const res = await apiFetch(`/api/chat/question?sessionId=${encodeURIComponent(conversationId)}`);
    if (!res.ok) return;
    const data = (await res.json()) as {
      question: {
        id: string;
        question: string;
        header?: string;
        kind: 'yes_no' | 'choice' | 'multi' | 'text';
        options: Array<{ label: string; description?: string }>;
        timeoutMs: number;
        askedAt: string;
      } | null;
    };
    const s = getState();
    if (s.conversationId !== conversationId) return; // switched away — stale
    if (!data.question) {
      if (s.pendingQuestion) setState({ ...s, pendingQuestion: null });
      return;
    }
    const { kind, ...rest } = data.question;
    setState({ ...s, pendingQuestion: { ...rest, questionKind: kind } });
  } catch {
    // Server unreachable — leave current state alone.
  }
}
